// Helpers for the Assist "Analyze Partner Crossover" banner. The banner sends the Assist & MTA
// page's current universal filters to the backend, which resolves the app-site and builds/runs a
// flattened (one row per install_id) assist export. The crossover export maps onto a single
// AppSite, so it is only available when exactly one app and one platform are selected.

export const ASSIST_CROSSOVER_LOG_TYPE = 'Assist';
export const ASSIST_CROSSOVER_API_TYPE = 'Trino';

// Backend's sentinel when no integrated app-site matches the chosen app + platform.
export const ASSIST_CROSSOVER_NO_APP_SITE_ERROR = 'no_integrated_app_site';

// Client-side guard: disable the export when the start date is more than ~3 months (90 days) ago.
// The backend's _validate_day_range enforces a date-range *span* limit (org-configurable
// num_of_days_limit, default 90 days), not a lookback floor. When end <= today this start-date check
// is at least as strict as that span limit, so the UI disables before the backend would reject.
export const ASSIST_CROSSOVER_MAX_LOOKBACK_DAYS = 90;

// Reasons the crossover export can't run.
export const ASSIST_CROSSOVER_DISABLED_REASONS = {
    APP_OS: 'app_os',
    DATE_RANGE: 'date_range',
};

const isStartDateTooOld = startDate => {
    if (!startDate) {
        return false;
    }
    const earliestAllowed = new Date();
    earliestAllowed.setHours(0, 0, 0, 0);
    earliestAllowed.setDate(earliestAllowed.getDate() - ASSIST_CROSSOVER_MAX_LOOKBACK_DAYS);
    // Parse the 'YYYY-MM-DD' start date as local midnight (not UTC, which `new Date(str)` would do)
    // so the comparison against the local-midnight earliestAllowed isn't off by a day across timezones.
    const [year, month, day] = startDate.split('-').map(Number);
    return new Date(year, month - 1, day) < earliestAllowed;
};

// Returns every reason the export is disabled (empty array when it can run). Both can apply at once:
//   'app_os'     -> not exactly one app and one platform selected (maps to a single AppSite)
//   'date_range' -> the start date is older than the allowed ~3-month window
export const getAssistCrossoverDisabledReasons = (filters, startDate) => {
    const reasons = [];
    if ((filters?.app?.length || 0) !== 1 || (filters?.os?.length || 0) !== 1) {
        reasons.push(ASSIST_CROSSOVER_DISABLED_REASONS.APP_OS);
    }
    if (isStartDateTooOld(startDate)) {
        reasons.push(ASSIST_CROSSOVER_DISABLED_REASONS.DATE_RANGE);
    }
    return reasons;
};

export const canRunAssistCrossover = (filters, startDate) =>
    getAssistCrossoverDisabledReasons(filters, startDate).length === 0;

// Build the POST body for /logs/assist_crossover_export from the page's current filters + dates.
// The backend resolves the app (external_uuid) + platform (OS label) to an integrated app-site and
// the source names to partners; countries are ISO codes.
export const buildAssistCrossoverFilters = (filters, startDate, endDate) => ({
    app: filters?.app?.[0]?.value,
    platform: filters?.os?.[0]?.label,
    sources: (filters?.source || []).map(source => source.value),
    countries: (filters?.country_field || []).map(country => country.value),
    'start-date': startDate,
    'end-date': endDate,
});
